import axios from 'axios';
import config from '../config';

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// URLs that should bypass the auth interceptor (they handle their own auth)
const PUBLIC_DOOR_URLS = ['/public/door/'];

// Check if URL should bypass auth interceptor
const shouldBypassAuthInterceptor = (url: string | undefined): boolean => {
  if (!url) return false;
  return PUBLIC_DOOR_URLS.some(publicUrl => url.includes(publicUrl));
};

// Setup axios interceptors
export const setupAxiosInterceptors = () => {
  // Request interceptor
  axios.interceptors.request.use(
    (config) => {
      // Skip auth header for public door URLs (they use their own session token)
      if (shouldBypassAuthInterceptor(config.url)) {
        return config;
      }

      // Prefer sessionStorage over localStorage (as per CLAUDE.md)
      const authUser = sessionStorage.getItem('authUser') || localStorage.getItem('authUser');
      if (authUser) {
        try {
          const parsed = JSON.parse(authUser);
          const token = parsed.token || parsed.data?.token;
          if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
          }
        } catch (e) {
          console.error('Error parsing authUser:', e);
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for token refresh
  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // Skip token refresh logic for public door URLs (they handle their own auth)
      if (shouldBypassAuthInterceptor(originalRequest?.url)) {
        return Promise.reject(error);
      }

      // If error is 401 and we haven't already tried to refresh
      if (error.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          // If already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            return axios(originalRequest);
          }).catch(err => {
            return Promise.reject(err);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        // Prefer sessionStorage over localStorage
        const authUser = sessionStorage.getItem('authUser') || localStorage.getItem('authUser');
        
        if (authUser) {
          try {
            const parsed = JSON.parse(authUser);
            const refreshToken = parsed.refreshToken || parsed.data?.refreshToken;
            
            if (refreshToken) {
              try {
                const response = await axios.post(`${config.api.API_URL}/auth/refresh`, { refreshToken });
                
                if (response.data.success) {
                  const { token, refreshToken: newRefreshToken, user } = response.data;
                  
                  // Update stored auth data
                  const existingAuth = parsed;
                  const updatedAuth = JSON.stringify({
                    ...existingAuth,
                    token,
                    refreshToken: newRefreshToken,
                    user
                  });
                  // Update both storages
                  sessionStorage.setItem('authUser', updatedAuth);
                  if (localStorage.getItem('authUser')) {
                    localStorage.setItem('authUser', updatedAuth);
                  }
                
                // Update default header
                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                
                processQueue(null, token);
                
                // Retry original request
                originalRequest.headers['Authorization'] = `Bearer ${token}`;
                return axios(originalRequest);
              }
            } catch (refreshError) {
              processQueue(refreshError, null);
              
              // Clear auth and redirect to login
              sessionStorage.removeItem('authUser');
              localStorage.removeItem('authUser');
              delete axios.defaults.headers.common['Authorization'];
              
              // Only redirect if not already on login page, public keys page, or public door page
              if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/public/keys') && !window.location.pathname.includes('/public/door')) {
                window.location.href = '/login?session=expired';
              }

              return Promise.reject(refreshError);
            } finally {
              isRefreshing = false;
            }
          }
          } catch (e) {
            console.error('Error parsing authUser:', e);
          }
        }

        // No refresh token available, clear auth and redirect
        sessionStorage.removeItem('authUser');
        localStorage.removeItem('authUser');
        delete axios.defaults.headers.common['Authorization'];

        if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/public/keys') && !window.location.pathname.includes('/public/door')) {
          window.location.href = '/login?session=expired';
        }
      }

      return Promise.reject(error);
    }
  );
};

// Auto-refresh token before expiration
export const startTokenRefreshTimer = () => {
  // Clear any existing timer
  const existingTimer = (window as any).tokenRefreshTimer;
  if (existingTimer) {
    clearInterval(existingTimer);
  }

  // Check token every 5 minutes
  (window as any).tokenRefreshTimer = setInterval(async () => {
    // Prefer sessionStorage over localStorage
    const authUser = sessionStorage.getItem('authUser') || localStorage.getItem('authUser');
    
    if (authUser) {
      try {
        const parsed = JSON.parse(authUser);
        const token = parsed.token || parsed.data?.token;
        const refreshToken = parsed.refreshToken || parsed.data?.refreshToken;
        
        if (token && refreshToken) {
          try {
            // Decode token to check expiration
            const tokenPayload = JSON.parse(atob(token.split('.')[1]));
            const expiresAt = tokenPayload.exp * 1000; // Convert to milliseconds
            const now = Date.now();
            const timeUntilExpiry = expiresAt - now;
            
            // If token expires in less than 10 minutes, refresh it
            if (timeUntilExpiry < 10 * 60 * 1000) {
              const response = await axios.post(`${config.api.API_URL}/auth/refresh`, { refreshToken });
              
              if (response.data.success) {
                const { token: newToken, refreshToken: newRefreshToken, user } = response.data;
                
                // Update stored auth data
                const updatedAuth = JSON.stringify({
                  ...parsed,
                  token: newToken,
                  refreshToken: newRefreshToken,
                  user
                });
                // Update both storages
                sessionStorage.setItem('authUser', updatedAuth);
                if (localStorage.getItem('authUser')) {
                  localStorage.setItem('authUser', updatedAuth);
                }
                
                // Update default header
                axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
              }
            }
          } catch (error) {
            console.error('Auto token refresh failed:', error);
            
            // If token is invalid or expired, redirect to login
            if (error instanceof DOMException || (error as any).message?.includes('Invalid token')) {
              sessionStorage.removeItem('authUser');
              localStorage.removeItem('authUser');
              delete axios.defaults.headers.common['Authorization'];
              
              if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/public/keys') && !window.location.pathname.includes('/public/door')) {
                window.location.href = '/login?session=expired';
              }
            }
          }
        }
      } catch (e) {
        console.error('Error parsing authUser in auto-refresh:', e);
      }
    }
  }, 5 * 60 * 1000); // Check every 5 minutes
};

// Stop the refresh timer
export const stopTokenRefreshTimer = () => {
  const existingTimer = (window as any).tokenRefreshTimer;
  if (existingTimer) {
    clearInterval(existingTimer);
    delete (window as any).tokenRefreshTimer;
  }
};